import { z } from 'zod';

/**
 * The shape of the build-time configuration, with no side effects.
 *
 * Kept apart from env.ts on purpose: that module validates the real
 * `import.meta.env` at load time and throws when it is incomplete, which makes
 * it unimportable from a test that has no .env file. The rules live here so
 * they can be exercised directly.
 */

/**
 * An unset variable and an empty one mean the same thing here: absent.
 *
 * Both sources of a blank value produce `""`, not `undefined` - an unfilled
 * line in `.env.example`, and a GitHub `vars.X || secrets.X` that resolves to
 * nothing. Without this, `.optional()` still rejects `""` and a build that
 * deliberately runs without App Check crashes at module load.
 */
const optionalString = () =>
  z.preprocess((v) => (v === '' ? undefined : v), z.string().min(1).optional());

/**
 * Only VITE_-prefixed values exist here - nothing secret ever reaches the
 * bundle.
 */
export const envSchema = z.object({
  VITE_FIREBASE_API_KEY: z.string().min(1),
  VITE_FIREBASE_AUTH_DOMAIN: z.string().min(1),
  VITE_FIREBASE_PROJECT_ID: z.string().min(1),
  VITE_FIREBASE_STORAGE_BUCKET: z.string().min(1),
  VITE_FIREBASE_MESSAGING_SENDER_ID: z.string().min(1),
  VITE_FIREBASE_APP_ID: z.string().min(1),

  /** Base URL of the Cloud Functions API, e.g. https://api.once.app */
  VITE_API_BASE_URL: z.string().url(),
  /** Base URL of the Cloud Run media gateway. */
  VITE_MEDIA_BASE_URL: z.string().url(),
  /** Public origin used to build shareable links. */
  VITE_PUBLIC_ORIGIN: z.string().url(),

  /**
   * reCAPTCHA v3 site key for App Check. Public by design.
   *
   * Optional so the app still boots without it - see firebase.ts, which logs
   * loudly and skips App Check. A production deployment should always set it.
   */
  VITE_RECAPTCHA_SITE_KEY: optionalString(),
  /** Local App Check debug token - development only. */
  VITE_APPCHECK_DEBUG_TOKEN: optionalString(),

  VITE_USE_EMULATORS: z
    .preprocess(
      (v) => (v === '' ? undefined : v),
      z.enum(['true', 'false']).optional(),
    )
    .transform((v) => v === 'true'),
});
